import { IndexationRequest, IndexationResult } from "../types/indexation.types";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";

/**
 * Calculate indexation using INTERNAL calculation engine
 *
 * This replaces the external Belgian government API with our own implementation
 * Benefits:
 * - No external dependency
 * - Offline capability
 * - Full control over calculations
 * - Audit trail and historical data
 * - Faster response times
 */
export const calculateIndexation = async (
  requestData: IndexationRequest,
  session: any
): Promise<IndexationResult> => {
  const response = await fetchWithAuth(
    `${API_URL}/rental/indexation/calculate`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestData)
    },
    session
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Indexation calculation error: ${errorText || response.statusText}`);
  }

  const result: IndexationResult = await response.json();
  return result;
};

/**
 * Build indexation request from contract data
 *
 * Determines the correct anniversary year based on last indexation date.
 * For missed indexations (never indexed or last indexed > 1 year ago),
 * uses the missed anniversary year instead of defaulting to current year.
 *
 * Belgian law: indexation anniversary is yearly on the same month/day as contract start.
 * If missed, the landlord can still index but only claim 3 months retroactive rent.
 */
export const buildIndexationRequest = (
  initPrice: number,
  signDate: Date | string,
  startDate: Date | string,
  epcLabel: string,
  lastIndexationDate?: Date | string | null
): IndexationRequest => {
  const now = new Date();
  const start = new Date(startDate);

  // Determine if the contract has been indexed recently (within the last year)
  const hasRecentIndexation = lastIndexationDate &&
    (now.getTime() - new Date(lastIndexationDate).getTime()) < (365 * 24 * 60 * 60 * 1000);

  // Create anniversary for current year (same month/day as contract start)
  const currentYearAnniversary = new Date(now.getFullYear(), start.getMonth(), start.getDate());

  let anniversaryYear = now.getFullYear();

  if (currentYearAnniversary > now && !hasRecentIndexation) {
    // Anniversary hasn't happened yet this year AND contract has a missed indexation
    // → use previous year's anniversary (the one that was missed)
    anniversaryYear = now.getFullYear() - 1;
  }
  // If anniversary is in the past this year and not recently indexed → use current year (correct)
  // If recently indexed → use current year (upcoming anniversary)

  return {
    initPrice,
    signDate: new Date(signDate).toISOString(),
    startDate: new Date(startDate).toISOString(),
    epcLabel,
    anniversaryYear
  };
};
